import React, { useState } from 'react';
import { Mail, MessageCircle, Send, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { formatApiError } from '../lib/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = ({ onOpenBooking }) => {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        message: form.message,
      });
      setSent(true);
      toast({
        title: 'Message sent',
        description: 'Thanks for reaching out — I will reply within 24 hours.',
      });
      setForm({ firstName: '', lastName: '', email: '', message: '' });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: formatApiError(
          err,
          'Could not send your message. Please try again or email me directly.'
        ),
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 bg-[#0a0a0a]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-amber-400 tracking-[0.32em] text-[12px] mb-5 font-medium">CONTACT</p>
            <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight">
              Get in <span className="text-amber-400 italic">touch</span>
            </h2>
            <p className="mt-6 text-white/65 text-[16.5px] leading-relaxed max-w-md">
              Have a question about lessons, scheduling, or payment? Send a message or reach out
              directly. I reply within 24 hours.
            </p>

            <div className="mt-10 space-y-5">
              <a href="mailto:baduk@kango9school.com" className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-full border border-amber-400/40 flex items-center justify-center group-hover:border-amber-400 group-hover:bg-amber-400/10 transition-all duration-300">
                  <Mail size={18} className="text-amber-400" />
                </div>
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-wider">Email</div>
                  <div className="text-white group-hover:text-amber-400 transition-colors">
                    baduk@kango9school.com
                  </div>
                </div>
              </a>
              <a
                href="https://wa.me/40727949772"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-full border border-amber-400/40 flex items-center justify-center group-hover:border-amber-400 group-hover:bg-amber-400/10 transition-all duration-300">
                  <MessageCircle size={18} className="text-amber-400" />
                </div>
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-wider">Whatsapp</div>
                  <div className="text-white group-hover:text-amber-400 transition-colors">
                    +40 727 949 772
                  </div>
                </div>
              </a>
            </div>

            <button
              onClick={onOpenBooking}
              className="mt-12 inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-amber-400 text-[#0a0a0a] hover:bg-amber-300 transition-colors duration-300 font-medium"
            >
              Book a lesson instead
            </button>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 lg:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-white/55 text-xs uppercase tracking-wider mb-2">
                    First Name
                  </label>
                  <input
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    type="text"
                    className="w-full bg-transparent border border-white/15 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-white/55 text-xs uppercase tracking-wider mb-2">
                    Last Name
                  </label>
                  <input
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    type="text"
                    className="w-full bg-transparent border border-white/15 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label className="block text-white/55 text-xs uppercase tracking-wider mb-2">
                  Email *
                </label>
                <input
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  type="email"
                  className="w-full bg-transparent border border-white/15 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>
              <div className="mt-5">
                <label className="block text-white/55 text-xs uppercase tracking-wider mb-2">
                  Message *
                </label>
                <textarea
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={5}
                  className="w-full bg-transparent border border-white/15 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-400 transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={sending || sent}
                className="mt-7 inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-amber-400 text-[#0a0a0a] hover:bg-amber-300 transition-colors duration-300 font-medium disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {sent ? (
                  <>
                    <CheckCircle2 size={18} /> Sent
                  </>
                ) : sending ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Message <Send size={16} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
